import { FastTrackReview } from "@/components/molecules/fast-track-review";
import { EmptyState } from "@/components/molecules/empty-state";
import { FilterPills } from "@/components/molecules/filter-pills";
import { ErrorState } from "@/components/molecules/error-state";
import { DataTable } from "@/components/molecules/tables/data-table";
import { Skeleton } from "@/components/ui/skeleton";
import { useFaqs } from "@/features/faq/hooks/use-faq";
import type { Faq } from "@/features/faq/types";
import { useTranslation } from "@/features/utils/i18n";
import { ChatCircleDotsIcon } from "@phosphor-icons/react";
import { useMemo, useState } from "react";
import { PageLayout } from "../layout";
import { useFaqBulkActions } from "./components/bulk-actions";
import { useFaqColumns } from "./components/columns";
import { CreateFaqDialog } from "./components/create-dialog";
import { DuplicatesDialog } from "./components/duplicates-dialog";

type FaqFilter = "all" | "needs_refinement" | "archived";

export function FaqPage() {
  const { t } = useTranslation("faq");
  const [chosenFilter, setFilter] = useState<FaqFilter | null>(null);

  // Each filter is backed by a dedicated query so counts stay accurate and the
  // "needs refinement" list comes straight from the backend.
  const activeQuery = useFaqs({ status: "active" });
  const needsQuery = useFaqs({ needsRefinement: true });
  const archivedQuery = useFaqs({ status: "archived" });

  // The page opens on what is waiting for review — unless nothing is, then on
  // the FAQ itself. Once the user picks a tab, that choice holds.
  const filter: FaqFilter =
    chosenFilter ??
    (needsQuery.isSuccess && !needsQuery.data?.faqs?.length
      ? "all"
      : "needs_refinement");

  const activeFaqs = useMemo(
    () => activeQuery.data?.faqs || [],
    [activeQuery.data],
  );
  const needsFaqs = useMemo(
    () => needsQuery.data?.faqs || [],
    [needsQuery.data],
  );
  const archivedFaqs = useMemo(
    () => archivedQuery.data?.faqs || [],
    [archivedQuery.data],
  );

  // Suggestions for the category field (existing categories across all FAQs).
  const categories = useMemo(() => {
    const set = new Set<string>();
    [...activeFaqs, ...archivedFaqs].forEach((f) => {
      if (f.category) set.add(f.category);
    });
    return Array.from(set).sort();
  }, [activeFaqs, archivedFaqs]);

  const columns = useFaqColumns(categories);
  const bulkActions = useFaqBulkActions();

  const view: { faqs: Faq[]; query: typeof activeQuery } =
    filter === "needs_refinement"
      ? { faqs: needsFaqs, query: needsQuery }
      : filter === "archived"
        ? { faqs: archivedFaqs, query: archivedQuery }
        : { faqs: activeFaqs, query: activeQuery };

  const filters: { key: FaqFilter; label: string; count: number }[] = [
    {
      key: "needs_refinement",
      label: t("filters.needsRefinement"),
      count: needsFaqs.length,
    },
    { key: "all", label: t("filters.all"), count: activeFaqs.length },
    {
      key: "archived",
      label: t("filters.archived"),
      count: archivedFaqs.length,
    },
  ];

  const showEmptyState =
    filter === "all" &&
    activeQuery.isSuccess &&
    activeFaqs.length === 0;

  return (
    <PageLayout
      title={t("title")}
      description={t("description")}
      actions={
        <div className="flex gap-2">
          <DuplicatesDialog />
          <CreateFaqDialog categories={categories} />
        </div>
      }
    >
      <FastTrackReview scope="faq" />

      <FilterPills
        label={t("title")}
        value={filter}
        onChange={setFilter}
        options={filters.map((f) => ({
          value: f.key,
          label: f.label,
          count: f.count,
        }))}
      />

      {view.query.isError ? (
        <ErrorState onRetry={view.query.refetch} />
      ) : showEmptyState ? (
        <EmptyState
          icon={ChatCircleDotsIcon}
          title={t("empty.title")}
          description={t("empty.description")}
          action={<CreateFaqDialog categories={categories} />}
        />
      ) : view.query.isLoading ? (
        <Skeleton className="h-96 w-full rounded-lg" />
      ) : (
        <DataTable
          columns={columns}
          data={view.faqs}
          total={view.faqs.length}
          bulkActions={bulkActions}
          emptyState={
            <EmptyState
              compact
              icon={ChatCircleDotsIcon}
              title={t("empty.filtered")}
              description={
                filter === "needs_refinement"
                  ? t("empty.needsRefinement")
                  : filter === "archived"
                    ? t("empty.archived")
                    : undefined
              }
            />
          }
        />
      )}
    </PageLayout>
  );
}
